// SolyticsTreeView.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import * as React from 'react';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';

import { styled, alpha } from '@mui/material/styles';

import { ReactComponent as RightArrowIcon } from '../assets/RightArrowIcon.svg';
import { ReactComponent as KeyboardArrowDownIcon } from '../assets/KeyboardArrowDownIcon.svg';
import SolyticsBox from './SolyticsBox';

/**
 * SolyticsTreeView - A hierarchical tree view component
 *
 * @component
 * @description
 * SolyticsTreeView wraps MUI's SimpleTreeView with custom styled TreeItems featuring
 * dashed connector lines, optional icons, and label info. Supports expand/collapse,
 * single/multi selection, and disabled nodes.
 *
 * @param {Array<Object>} [data=[]] - Tree data: [{ id, label, labelIcon?, labelInfo?, disabled?, bgColor?, children? }]
 * @param {Array<string>} [expanded=[]] - Initially expanded node ids
 * @param {string|null} [selected=null] - Initially selected node id
 * @param {function} [onNodeToggle] - Called when nodes are expanded/collapsed
 * @param {function} [onNodeSelect] - Called when a node is selected
 * @param {boolean} [multiSelect=false] - If true, allows multiple node selection
 * @param {boolean} [disabledItemsFocusable=false] - If true, disabled items can receive focus
 * @param {string} [ariaLabel='Solytics Tree View'] - Accessible label for the tree region
 *
 * @returns {React.ReactElement} A tree view component
 */

const ExpandIcon = (props) => (
  <RightArrowIcon {...props} style={{ fontSize: 20 }} />
);

const CollapseIcon = (props) => (
  <KeyboardArrowDownIcon {...props} style={{ fontSize: 20 }} />
);

const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },

    '&.Mui-selected': {
      backgroundColor:
        theme.palette.selection?.main || theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
  },

  [`& .${treeItemClasses.label}`]: {
    fontSize: theme.typography.body2.fontSize,
  },

  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 16,
    paddingLeft: 16,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.35)}`,
  },
}));

export const SolyticsTreeView = ({
  data = [],

  expanded = [],
  selected = null,

  onNodeToggle = () => {},
  onNodeSelect = () => {},

  multiSelect = false,
  disabledItemsFocusable = false,

  ariaLabel = 'Solytics Tree View',
}) => {
  const renderTree = (nodes = []) =>
    nodes.map((node) => (
      <CustomTreeItem
        key={node.id}
        itemId={node.id}
        disabled={node.disabled}
        label={
          <SolyticsBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {node.labelIcon && (
              <SolyticsBox
                sx={(theme) => ({
                  display: 'flex',
                  color: theme.palette.text.secondary,
                })}
              >
                {node.labelIcon}
              </SolyticsBox>
            )}

            <span>{node.label}</span>

            {node.labelInfo && (
              <SolyticsBox
                sx={(theme) => ({
                  marginLeft: 'auto',
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.secondary,
                })}
              >
                {node.labelInfo}
              </SolyticsBox>
            )}
          </SolyticsBox>
        }
        sx={(theme) => ({
          '& .MuiTreeItem-content.Mui-selected': {
            backgroundColor: node.bgColor || theme.palette.selection?.main,
          },
        })}
      >
        {Array.isArray(node.children) && renderTree(node.children)}
      </CustomTreeItem>
    ));

  return (
    <SolyticsBox
      role="region"
      aria-label={ariaLabel}
      sx={(theme) => ({
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
      })}
    >
      <SimpleTreeView
        expandedItems={expanded}
        selectedItems={selected}
        onExpandedItemsChange={onNodeToggle}
        onSelectedItemsChange={onNodeSelect}
        multiSelect={multiSelect}
        disabledItemsFocusable={disabledItemsFocusable}
        slots={{
          expandIcon: ExpandIcon,
          collapseIcon: CollapseIcon,
        }}
        slotProps={{
          expandIcon: {
            sx: (theme) => ({
              color: theme.palette.text.secondary,
            }),
          },
          collapseIcon: {
            sx: (theme) => ({
              color: theme.palette.text.secondary,
            }),
          },
        }}
      >
        {renderTree(data)}
      </SimpleTreeView>
    </SolyticsBox>
  );
};
